import React, { Suspense, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Switch, FormControlLabel, Button } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import DialogSpritePacker from "../dialogSpritePacker";
import DialogImageOptimizer from "./dialogImageOptimizer";

const DialogGlbOptimizer = React.lazy(() => import("./dialogGlbOptimizer"));

const STORAGE_KEY = "settings.autoCompress";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    marginLeft: "21.5rem",
    paddingBottom: "1rem",
    backgroundColor: "#404040",
  },
  content: {
    flexGrow: 1,
    backgroundColor: "#404040",
    color: "white",
    marginTop: "0.5%",
    marginRight: "1rem",
  },
  card: {
    margin: "0rem 0rem 0.5rem 0.5rem",
  },
  cardWrapper: {
    padding: "2rem",
  },
  cardTitleText: {
    fontSize: 26,
    fontWeight: 540,
    marginBottom: "1.5rem",
  },
  settingRow: {
    display: "flex",
    alignItems: "flex-start",
    margin: "1rem 0",
  },
  settingInfo: {
    marginLeft: "1rem",
  },
  settingDescription: {
    marginTop: "0.25rem",
    maxWidth: "500px",
    fontSize: "1rem",
  },
}));

export function getAutoCompress() {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === null ? true : stored === "true";
}

export default function ToolSettings({ userCompany }) {
  const classes = useStyles();
  const [autoCompress, setAutoCompress] = useState(getAutoCompress);
  const [isPackerOpen, setIsPackerOpen] = useState(false);
  const [isOptimizerOpen, setIsOptimizerOpen] = useState(false);
  const [isGlbOptimizerOpen, setIsGlbOptimizerOpen] = useState(false);
  const canUsePacker = userCompany?.isPermittedSpritePacker === true;

  const handleChange = (e) => {
    const val = e.target.checked;
    setAutoCompress(val);
    localStorage.setItem(STORAGE_KEY, String(val));
  };

  return (
    <div className={classes.root}>
      <main className={classes.content}>
        <Card className={classes.card}>
          <div className={classes.cardWrapper}>
            <Typography className={classes.cardTitleText} align="left">
              Image Upload
            </Typography>
            <div className={classes.settingRow}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoCompress}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="Smart Auto-Optimize"
              />
              <div className={classes.settingInfo}>
                <Typography className={classes.settingDescription}>
                  Automatically optimizes images on upload using server-side optimization
                  (PNG: ~80% smaller, JPEG: ~50% smaller). Visually lossless. Turn off to
                  upload the original file without any processing.
                </Typography>
              </div>
            </div>
          </div>
        </Card>

        <Card className={classes.card}>
          <div className={classes.cardWrapper}>
            <Typography className={classes.cardTitleText} align="left">
              Image Optimizer
            </Typography>
            <div className={classes.settingRow}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsOptimizerOpen(true)}
              >
                Open Image Optimizer
              </Button>
              <div className={classes.settingInfo}>
                <Typography className={classes.settingDescription}>
                  Make your PNG and JPEG images smaller without changing their appearance.
                  Upload multiple images at once, then download the optimized files individually.
                </Typography>
              </div>
            </div>
          </div>
        </Card>

        <DialogImageOptimizer
          open={isOptimizerOpen}
          onClose={() => setIsOptimizerOpen(false)}
        />

        <Card className={classes.card}>
          <div className={classes.cardWrapper}>
            <Typography className={classes.cardTitleText} align="left">GLB Optimizer</Typography>
            <div className={classes.settingRow}>
              <Button variant="contained" color="primary" onClick={() => setIsGlbOptimizerOpen(true)}>
                Open GLB Optimizer
              </Button>
              <div className={classes.settingInfo}>
                <Typography className={classes.settingDescription}>
                  Make your 3D characters smaller. Resize textures, adjust quality and reduce
                  triangle counts, then preview and download the optimized GLB.
                </Typography>
              </div>
            </div>
          </div>
        </Card>
        {isGlbOptimizerOpen && <Suspense fallback={null}>
          <DialogGlbOptimizer open onClose={() => setIsGlbOptimizerOpen(false)} />
        </Suspense>}

        {canUsePacker && (
          <Card className={classes.card}>
            <div className={classes.cardWrapper}>
              <Typography className={classes.cardTitleText} align="left">
                Sprite Sheet Packer
              </Typography>
              <div className={classes.settingRow}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setIsPackerOpen(true)}
                >
                  Open Packer
                </Button>
                <div className={classes.settingInfo}>
                  <Typography className={classes.settingDescription}>
                    Combine individual images into a single sprite sheet with a matching JSON
                    atlas. Drop your frames in, adjust padding and scale, then download the
                    packed <strong>.png</strong> and <strong>.json</strong> ready to use.
                  </Typography>
                </div>
              </div>
            </div>
          </Card>
        )}

        {canUsePacker && (
          <DialogSpritePacker
            open={isPackerOpen}
            onClose={() => setIsPackerOpen(false)}
            showUpload={false}
          />
        )}
      </main>
    </div>
  );
}
